import { gql, useApolloClient } from '@apollo/client';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  HStack,
  Skeleton,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  CompanyDocumentType,
  CompanyDraftStatus,
  IdentityType,
  SpvDraftDocument,
  useGetBankIdentitiesByCompanyQuery,
  useGetSpvDraftQuery,
} from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

import { SPV_DOCUMENT_TYPE_TO_SIGN, getIdentityBadge } from '../../../utils';
import { SPVDraftIdentitiesList } from './SPVDraftIdentitiesList/SPVDraftIdentitiesList';

type Props = unknown;

/**
 * SPVMatriculationIdentity.
 * Identity verification accordion.
 *
 * @returns {JSX.Element}
 */
export function SPVMatriculationIdentity(): JSX.Element {
  const i18nBadge = useTranslations('Badge');
  const i18nSPVIdentity = useTranslations('SPVMatriculationIdentity');
  const [isPollingStarted, setIsPollingStarted] = useState<boolean>(false);
  const [isAccordionLoading, setIsAccordionLoading] = useState<boolean>(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);

  const { id = '' } = useParams();

  const spvDraftReq = useGetSpvDraftQuery({ variables: { id } });
  const { data: spvData, loading, error } = spvDraftReq;
  const isIndividual =
    spvData?.spvDraft.content.representatives[0].type === IdentityType.INDIVIDUAL;
  const areAllDocumentsSigned = spvData?.spvDraft.status === CompanyDraftStatus.SIGNED;

  const bankIdentitiesReq = useGetBankIdentitiesByCompanyQuery({
    variables: {
      companyId: spvData?.spvDraft?.company?.id || '',
    },
    skip: !spvData?.spvDraft?.company?.id,
  });
  const bankIdentities = bankIdentitiesReq.data?.getBankIdentitiesByCompany || [];
  const hasBankIdentities = !!bankIdentities.length;

  /* ** Retrieve created spv draft documents from cache ** */
  const client = useApolloClient();
  const cachedSpvDraft = client.readFragment({
    id: `CompanyDraft:${id}`,
    fragment: gql`
      fragment CompanyDraftInfos on CompanyDraft {
        documents {
          type
          document {
            status
          }
        }
      }
    `,
  });
  const spvDraftDocuments: SpvDraftDocument[] = cachedSpvDraft?.documents;

  const areAllDocumentsCreated =
    spvDraftDocuments &&
    SPV_DOCUMENT_TYPE_TO_SIGN.every(
      (docType) =>
        spvDraftDocuments?.some((doc) => {
          if (!isIndividual && docType === CompanyDocumentType.DNC) return true;
          return doc.type === docType;
        }),
    );

  useEffect(() => {
    /* ** Get real time update on accordion open/close status ** */
    const activeStep =
      spvData?.spvDraft?.status === CompanyDraftStatus.SIGNED && hasBankIdentities ? true : false;

    setIsAccordionOpen(activeStep);
  }, [spvData, hasBankIdentities]);

  useEffect(() => {
    if (!bankIdentitiesReq) return;

    if (!isPollingStarted && areAllDocumentsCreated && !hasBankIdentities) {
      /* ** Start polling on spv draft when last document has pending signature ** */
      setIsPollingStarted(true);
      spvDraftReq.startPolling(1000);

      setTimeout(() => {
        bankIdentitiesReq.stopPolling();
        spvDraftReq.stopPolling();
        setIsAccordionLoading(false);
      }, 20_000);
    }

    if (!isAccordionLoading && areAllDocumentsSigned) {
      /* ** Start accordion spinner once documents are all signed ** */
      bankIdentitiesReq.startPolling(2000);
      spvDraftReq.stopPolling();
      setIsAccordionLoading(true);
    }

    if (hasBankIdentities) {
      bankIdentitiesReq.stopPolling();
      spvDraftReq.stopPolling();
      setIsAccordionLoading(false);
    }
  }, [
    areAllDocumentsCreated,
    areAllDocumentsSigned,
    bankIdentitiesReq,
    hasBankIdentities,
    isAccordionLoading,
    isPollingStarted,
    spvDraftReq,
  ]);

  if (loading) {
    return <Skeleton h="65px" />;
  }

  if (error) {
    return <ErrorQueryCard h="65px" />;
  }

  if (!spvData) {
    return <ErrorQueryCard h="65px" />;
  }

  const badgeValue = getIdentityBadge(spvData.spvDraft, bankIdentities);

  const handleAccordionOpen = (): void => {
    setIsAccordionOpen(!isAccordionOpen);
  };

  return (
    <Accordion allowMultiple index={isAccordionOpen ? [0] : [-1]} onChange={handleAccordionOpen}>
      <AccordionItem isDisabled={!hasBankIdentities}>
        <AccordionButton>
          <AccordionIcon boxSize="8" />
          <Box flex="1" p="2" textAlign="left">
            <Text fontSize="xl" fontWeight="semibold">
              {i18nSPVIdentity('Title')}
            </Text>
          </Box>
          <Badge colorScheme={badgeValue.color}>
            <HStack alignItems="center" spacing="1">
              {isAccordionLoading && <Spinner size="xs" speed="0.7s" />}
              <Text>{i18nBadge(badgeValue.label as keyof Messages['Badge'])}</Text>
            </HStack>
          </Badge>
        </AccordionButton>
        <AccordionPanel>
          <Stack spacing="4">
            <SPVDraftIdentitiesList bankIdentities={bankIdentities} />
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

export type SPVMatriculationIdentityProps = Props;
